// src/utils/staveRenderMonitor.js
// Monitoring du rendu des portées : prouve (stave_render_ok) ou infirme (stave_render_clipped /
// stave_render_empty) que toutes les notes affichées sont visibles ENTIÈREMENT, et remonte l'appareil,
// le navigateur et la taille d'écran quand ce n'est pas le cas. Lecture : Firestore `appLogs`
// (admin) et section « Rendu des portées » du rapport hebdo (scripts/generate-monitoring-report.js).
//
// Principe : pour chaque note du SVG (g.vf-stavenote) on compare son rectangle réel à l'écran
// (getBoundingClientRect, transformations CSS comprises) à celui de chaque ancêtre qui peut la
// rogner — le SVG lui-même, les conteneurs overflow:hidden/clip, et la largeur du viewport.
//   - ancêtre « hard »   (hidden/clip, ou auto/scroll sans rien à défiler) : tout dépassement = note coupée
//   - ancêtre « scroll » (auto/scroll avec contenu qui dépasse) : une note hors cadre est juste
//     défilée (normal), elle est exclue du contrôle ; elle n'est jamais comptée comme coupée.
import { useEffect } from 'react';
import MonitoringService from '../services/MonitoringService';
import { auth } from '../firebaseConfig';

const TOLERANCE_PX = 2;      // arrondis sous-pixel / antialiasing
const SETTLE_MS = 1200;      // laisser finir les setTimeout de rendu (50ms) + fitScale + transitions
const MAX_OK_PER_SESSION = 10;
const MAX_ANOMALY_PER_SESSION = 20;
const OCCLUDED_MIN_COVER = 0.25; // une note est « masquée » si au moins 25 % de sa surface est recouverte

function axisMode(cs, el, axis) {
  const v = axis === 'x' ? cs.overflowX : cs.overflowY;
  if (v === 'hidden' || v === 'clip') return 'hard';
  if (v === 'auto' || v === 'scroll') {
    const scrollable = axis === 'x'
      ? el.scrollWidth > el.clientWidth + 1
      : el.scrollHeight > el.clientHeight + 1;
    return scrollable ? 'scroll' : 'hard';
  }
  return 'none';
}

// Décrit un élément pour les logs : balise, id, 3 premières classes, overflow calculé, hauteur.
// Sert à identifier QUEL conteneur rogne la portée (champ `clipper` de stave_render_clipped).
function describeEl(el) {
  try {
    const cls = typeof el.className === 'string'
      ? el.className.trim().split(/\s+/).filter(Boolean).slice(0, 3).join('.') : '';
    const cs = window.getComputedStyle(el);
    return `${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ''}${cls ? `.${cls}` : ''}`
      .slice(0, 80) + ` [ox:${cs.overflowX} oy:${cs.overflowY} h:${Math.round(el.getBoundingClientRect().height)}]`;
  } catch { return null; }
}

// Liste, du plus proche au plus éloigné, les éléments susceptibles de rogner le contenu du SVG.
function collectClippers(svg) {
  const clippers = [];
  for (let el = svg; el && el.nodeType === 1; el = el.parentElement) {
    if (el === document.documentElement) break;
    const cs = window.getComputedStyle(el);
    const x = axisMode(cs, el, 'x');
    const y = axisMode(cs, el, 'y');
    if (x === 'none' && y === 'none') continue;
    clippers.push({ rect: el.getBoundingClientRect(), x, y, el });
  }
  // La largeur de l'écran rogne toujours (sauf scroll horizontal de page, quasi inexistant ici).
  clippers.push({
    rect: { left: 0, right: document.documentElement.clientWidth, top: -Infinity, bottom: Infinity },
    x: 'hard',
    y: 'none',
  });
  return clippers;
}

// Éléments posés PAR-DESSUS la portée et marqués `data-occluder="<nom>"` : drumkit, bulles de Marcel…
// Un élément invisible (display:none, visibility:hidden, opacity 0, taille nulle) ne masque rien.
function collectOccluders() {
  const out = [];
  document.querySelectorAll('[data-occluder]').forEach((el) => {
    try {
      const cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return;
      const rect = el.getBoundingClientRect();
      if (!(rect.width > 0 && rect.height > 0)) return;
      out.push({ name: el.getAttribute('data-occluder'), rect });
    } catch { /* élément détaché : ignoré */ }
  });
  return out;
}

// Part (0–1) de la surface de `r` recouverte par `o`.
function coverFraction(r, o) {
  const w = Math.min(r.right, o.right) - Math.max(r.left, o.left);
  const h = Math.min(r.bottom, o.bottom) - Math.max(r.top, o.top);
  if (!(w > 0 && h > 0)) return 0;
  return (w * h) / (r.width * r.height);
}

/**
 * Mesure le rendu d'un SVG de portée. Pure (pas d'effet de bord), exportée pour les tests.
 * @returns {{notes, cutNotes, hiddenNotes, scrolledOut, cutLeft, cutRight, cutTop, cutBottom, svgW, containerW}}
 */
export function measureStaveRender(svg) {
  const noteEls = Array.from(svg.querySelectorAll('g.vf-stavenote'));
  const clippers = collectClippers(svg);
  const sr = svg.getBoundingClientRect();
  const res = {
    notes: 0, cutNotes: 0, hiddenNotes: 0, scrolledOut: 0,
    cutLeft: 0, cutRight: 0, cutTop: 0, cutBottom: 0,
    occludedNotes: 0, occludedLow: 0, occludedMaxPct: 0,
    svgW: Math.round(sr.width),
    containerW: Math.round(svg.parentElement?.getBoundingClientRect().width || 0),
  };

  const occluders = collectOccluders();
  const occludedBy = new Set();
  let worstCut = 0;
  noteEls.forEach(el => {
    const r = el.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) return; // note non dessinée
    res.notes += 1;

    // 1) Hors cadre d'un conteneur qui défile → simplement défilée, hors périmètre
    const scrolledOut = clippers.some(c =>
      (c.x === 'scroll' && (r.right <= c.rect.left || r.left >= c.rect.right)) ||
      (c.y === 'scroll' && (r.bottom <= c.rect.top || r.top >= c.rect.bottom)));
    if (scrolledOut) { res.scrolledOut += 1; return; }

    // 1bis) Note recouverte par un élément posé au-dessus (drumkit, bulle de Marcel)
    let worstCover = 0;
    let coverName = null;
    occluders.forEach(o => {
      const f = coverFraction(r, o.rect);
      if (f > worstCover) { worstCover = f; coverName = o.name; }
    });
    if (worstCover >= OCCLUDED_MIN_COVER) {
      res.occludedNotes += 1;
      if (/note-low/.test(el.id || '')) res.occludedLow += 1; // voix basse : grosse caisse, caisse claire…
      res.occludedMaxPct = Math.max(res.occludedMaxPct, Math.round(worstCover * 100));
      occludedBy.add(coverName);
    }

    // 2) Dépassement par rapport aux conteneurs qui rognent pour de bon
    let cl = 0, cr = 0, ct = 0, cb = 0;
    clippers.forEach(c => {
      let own = 0;
      if (c.x === 'hard') {
        cl = Math.max(cl, c.rect.left - r.left);
        cr = Math.max(cr, r.right - c.rect.right);
        own = Math.max(own, c.rect.left - r.left, r.right - c.rect.right);
      }
      if (c.y === 'hard') {
        ct = Math.max(ct, c.rect.top - r.top);
        cb = Math.max(cb, r.bottom - c.rect.bottom);
        own = Math.max(own, c.rect.top - r.top, r.bottom - c.rect.bottom);
      }
      // Élément responsable = celui qui rogne le plus, toutes notes confondues
      if (own > TOLERANCE_PX && own > worstCut && c.el) { worstCut = own; res.clipper = describeEl(c.el); }
    });
    const worst = Math.max(cl, cr, ct, cb);
    if (worst > TOLERANCE_PX) {
      res.cutNotes += 1;
      const w = Math.max(r.width, r.height);
      if (worst >= w * 0.95) res.hiddenNotes += 1; // entièrement invisible
      res.cutLeft = Math.max(res.cutLeft, Math.round(cl));
      res.cutRight = Math.max(res.cutRight, Math.round(cr));
      res.cutTop = Math.max(res.cutTop, Math.round(ct));
      res.cutBottom = Math.max(res.cutBottom, Math.round(cb));
    }
  });
  if (occludedBy.size) {
    res.occludedBy = Array.from(occludedBy).sort().join(',');
    res.occluders = occluders.slice(0, 3)
      .map(o => `${o.name}@${Math.round(o.rect.left)},${Math.round(o.rect.top)} ${Math.round(o.rect.width)}x${Math.round(o.rect.height)}`).join(' ; ');
  }
  return res;
}

// Identifiant du bundle JS en ligne (main.<hash>.js) : permet de relier une anomalie à un déploiement.
export function buildId() {
  try {
    const src = Array.from(document.scripts).map(x => x.src).find(u => /\/static\/js\/main\.[^/]+\.js/.test(u));
    return src ? src.split('/').pop().replace(/\.js$/, '') : 'dev';
  } catch { return null; }
}

// Modèle de l'appareil et version d'OS : l'UA de Chrome Android est « réduit » (modèle masqué, « K »),
// seule l'API Client Hints les donne. Chromium uniquement (Safari/Firefox : absent, champs omis).
// Chargé une fois au démarrage, asynchrone : les événements émis après la mise en page (>1 s) l'ont déjà.
let hintedDevice = {};
try {
  navigator.userAgentData?.getHighEntropyValues?.(['model', 'platformVersion'])
    .then(h => {
      hintedDevice = {
        ...(h.model ? { model: String(h.model).slice(0, 60) } : {}),
        ...(h.platformVersion ? { osVersion: String(h.platformVersion).slice(0, 20) } : {}),
      };
    })
    .catch(() => { /* indisponible : on s'en passe */ });
} catch { /* le monitoring ne casse jamais l'app */ }

export function deviceInfo() {
  const uad = navigator.userAgentData;
  return {
    ...hintedDevice,
    innerW: window.innerWidth,
    innerH: window.innerHeight,
    screenW: window.screen?.width,
    screenH: window.screen?.height,
    dpr: window.devicePixelRatio,
    vvScale: Math.round((window.visualViewport?.scale || 1) * 100) / 100, // zoom pincé
    orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
    platform: uad?.platform || navigator.platform || '',
    uaMobile: uad?.mobile ?? null,
    touchPoints: navigator.maxTouchPoints || 0,
    standalone: !!(window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone),
    lang: navigator.language,
    build: buildId(),
  };
}

function sessionCount(key) {
  try { return parseInt(sessionStorage.getItem(key) || '0', 10); } catch { return 0; }
}
function sessionBump(key) {
  try { sessionStorage.setItem(key, String(sessionCount(key) + 1)); } catch { /* ignore */ }
}
function sessionOnce(key) {
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
  } catch { /* storage indisponible : on laisse passer, le dédoublonnage du service fait le reste */ }
  return true;
}

/**
 * Contrôle + remontée d'un événement. À appeler une fois la mise en page stabilisée.
 * @param {HTMLElement} container élément qui contient le <svg>
 * @param {{component: string, measures?: number, notation?: string}} ctx
 */
export function reportStaveRender(container, ctx) {
  try {
    const svg = container?.querySelector('svg');
    if (!svg) return;
    const m = measureStaveRender(svg);

    // « Écoute-reproduit » : la portée est vide volontairement pendant l'écoute (l'élève reproduit
    // de mémoire, la réponse n'apparaît qu'ensuite) — ce n'est pas une anomalie de rendu.
    if (m.notes === 0 && window.location.pathname.startsWith('/ecoute-reproduit/')) return;
    const empty = m.notes === 0 && (ctx.measures || 1) > 0;
    const clipped = m.cutNotes > 0;
    const occluded = m.occludedNotes > 0;
    const type = empty ? 'stave_render_empty' : (clipped ? 'stave_render_clipped' : 'stave_render_ok');

    const page = window.location.pathname;
    const size = `${window.innerWidth}x${window.innerHeight}`;
    const uid = auth.currentUser?.uid || null;

    const send = (evType, guardKey, dedupeKey) => {
      const isOk = evType === 'stave_render_ok';
      if (sessionCount(isOk ? 'render:okCount' : 'render:badCount') >= (isOk ? MAX_OK_PER_SESSION : MAX_ANOMALY_PER_SESSION)) return;
      if (!sessionOnce(guardKey)) return;
      sessionBump(isOk ? 'render:okCount' : 'render:badCount');
      const params = {
        exerciseId: dedupeKey, // clé de dédoublonnage du service
        component: ctx.component,
        page,
        measures: ctx.measures ?? null,
        notation: ctx.notation ?? null,
        ...deviceInfo(),
        ...m,
      };
      if (process.env.NODE_ENV !== 'production') console.log(`[stave-render] ${evType}`, params);
      if (isOk) MonitoringService.info(evType, params, uid);
      else MonitoringService.warning(evType, params, uid);
    };

    // « OK » prouve que toutes les notes sont entièrement visibles : jamais émis si une note est masquée.
    if (!(type === 'stave_render_ok' && occluded)) {
      send(
        type,
        type === 'stave_render_ok'
          ? `render:ok:${ctx.component}:${page}:${size}`
          : `render:${type}:${ctx.component}:${page}:${size}:${m.cutNotes}:${m.notes}`,
        `${page}|${size}|${ctx.component}|${m.cutNotes}`,
      );
    }
    // Notes recouvertes par le drumkit ou une bulle de Marcel : événement distinct (peut s'ajouter à « coupée »).
    if (occluded) {
      send(
        'stave_render_occluded',
        `render:stave_render_occluded:${ctx.component}:${page}:${size}:${m.occludedBy}:${m.occludedNotes}`,
        `${page}|${size}|${ctx.component}|occ:${m.occludedBy}`,
      );
    }
  } catch {
    /* le monitoring ne doit jamais casser l'affichage */
  }
}

/**
 * Hook : contrôle le rendu une fois stabilisé, puis à chaque changement de `deps`
 * (nouvelle partition, nouvelle largeur…) et à chaque redimensionnement / rotation.
 */
export function useStaveRenderMonitor(divRef, { component, measures, notation }, deps) {
  useEffect(() => {
    let timer = null;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => reportStaveRender(divRef.current, { component, measures, notation }), SETTLE_MS);
    };
    schedule();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divRef, component, measures, notation, ...deps]);
}
